import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  googleSession: (session_id) => api.post('/auth/session', { session_id }),
  refresh: () => api.post('/auth/refresh'),
};

// Listings
export const listingsAPI = {
  getAll: (params) => api.get('/listings', { params }),
  getOne: (id) => api.get(`/listings/${id}`),
  create: (data) => api.post('/listings', data),
  update: (id, data) => api.put(`/listings/${id}`, data),
  delete: (id) => api.delete(`/listings/${id}`),
  getMine: () => api.get('/my-listings'),
  trackImpressions: (listing_ids) => api.post('/listings/track-impressions', { listing_ids }),
};

// File Upload
export const uploadAPI = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/api/upload`, formData, {
      withCredentials: true,
    });
  },
};

// Messages
export const messagesAPI = {
  send: (data) => api.post('/messages', data),
  getConversations: () => api.get('/conversations'),
  getMessages: (convId) => api.get(`/messages/${convId}`),
};

// Reviews
export const reviewsAPI = {
  create: (data) => api.post('/reviews', data),
  getForUser: (userId) => api.get(`/reviews/${userId}`),
};

// Reports
export const reportsAPI = {
  create: (data) => api.post('/reports', data),
};

// Users
export const usersAPI = {
  getProfile: (userId) => api.get(`/users/${userId}`),
  updateProfile: (data) => api.put('/users/profile', data),
  getNotificationPrefs: () => api.get('/users/notification-prefs'),
  updateNotificationPrefs: (data) => api.put('/users/notification-prefs', data),
  upgradeBusiness: (data) => api.put('/users/upgrade-business', data),
};

// Watchlist
export const watchlistAPI = {
  getAll: () => api.get('/watchlist'),
  getIds: () => api.get('/watchlist/ids'),
  add: (listingId) => api.post(`/watchlist/${listingId}`),
  remove: (listingId) => api.delete(`/watchlist/${listingId}`),
};

// Recently Viewed
export const recentlyViewedAPI = {
  record: (listingId) => api.post(`/recently-viewed/${listingId}`),
  getAll: (limit = 12) => api.get('/recently-viewed', { params: { limit } }),
};

// Saved Searches
export const savedSearchesAPI = {
  getAll: () => api.get('/saved-searches'),
  create: (data) => api.post('/saved-searches', data),
  delete: (id) => api.delete(`/saved-searches/${id}`),
};

// Verification
export const verificationAPI = {
  submit: (data) => api.post('/verification/request', data),
  getStatus: () => api.get('/verification/status'),
};

// Categories & Cities
export const metaAPI = {
  getCategories: () => api.get('/categories'),
  getCities: () => api.get('/cities'),
  getPromotionTiers: () => api.get('/promotion-tiers'),
};

// Payments
export const paymentsAPI = {
  promotionCheckout: (data) => api.post('/promotions/checkout', data),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  myPromotions: () => api.get('/my-promotions'),
};

// Admin
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (params) => api.get('/admin/users', { params }),
  banUser: (userId) => api.put(`/admin/users/${userId}/ban`),
  verifyUser: (userId) => api.put(`/admin/users/${userId}/verify`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getListings: (params) => api.get('/admin/listings', { params }),
  removeListing: (listingId) => api.delete(`/admin/listings/${listingId}`),
  getReports: (params) => api.get('/admin/reports', { params }),
  updateReport: (reportId, data) => api.put(`/admin/reports/${reportId}`, data),
  getTransactions: (params) => api.get('/admin/transactions', { params }),
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  // Promotion Tiers CRUD
  getPromotionTiers: () => api.get('/admin/promotion-tiers'),
  createPromotionTier: (data) => api.post('/admin/promotion-tiers', data),
  updatePromotionTier: (id, data) => api.put(`/admin/promotion-tiers/${id}`, data),
  deletePromotionTier: (id) => api.delete(`/admin/promotion-tiers/${id}`),
  // Promotion Analytics
  getPromotionAnalytics: () => api.get('/admin/promotion-analytics'),
  // Promotions Management
  getPromotions: (params) => api.get('/admin/promotions', { params }),
  createPromotion: (data) => api.post('/admin/promotions', data),
  expirePromotion: (id) => api.put(`/admin/promotions/${id}/expire`),
  extendPromotion: (id, data) => api.put(`/admin/promotions/${id}/extend`, data),
  // Business Accounts
  getBusinessAccounts: (params) => api.get('/admin/business-accounts', { params }),
  revokeBusinessAccount: (userId) => api.put(`/admin/business-accounts/${userId}/revoke`),
  // Verifications
  getVerifications: (params) => api.get('/admin/verifications', { params }),
  approveVerification: (id) => api.put(`/admin/verifications/${id}/approve`),
  rejectVerification: (id, data) => api.put(`/admin/verifications/${id}/reject`, data),
  // Business Ads
  getBusinessAds: (params) => api.get('/admin/business-ads', { params }),
  createBusinessAd: (data) => api.post('/admin/business-ads', data),
  updateBusinessAd: (id, data) => api.put(`/admin/business-ads/${id}`, data),
  deleteBusinessAd: (id) => api.delete(`/admin/business-ads/${id}`),
  toggleBusinessAd: (id) => api.put(`/admin/business-ads/${id}/toggle`),
  getBusinessAdAnalytics: () => api.get('/admin/business-ads/analytics'),
  sendBusinessAdReport: () => api.post('/admin/business-ads/send-report'),
};

// Business Ads (public)
export const businessAdsAPI = {
  getPublic: (limit = 10) => api.get('/business-ads/public', { params: { limit } }),
  trackImpression: (id) => api.post(`/business-ads/${id}/impression`),
  trackClick: (id) => api.post(`/business-ads/${id}/click`),
  getCategories: () => api.get('/business-ads/categories'),
};

// Analytics (heartbeat + admin)
export const analyticsAPI = {
  heartbeat: () => api.post('/activity/heartbeat'),
  getOverview: () => api.get('/admin/analytics/overview'),
  getActivity: (params) => api.get('/admin/analytics/activity', { params }),
  getCountries: () => api.get('/admin/analytics/countries'),
  getDevices: () => api.get('/admin/analytics/devices'),
};

// Public settings
export const settingsAPI = {
  getPublic: () => api.get('/settings/public'),
};

export default api;
